import React, { PureComponent, useContext } from 'react';
import { connect } from 'react-redux';
import fileExtension from 'file-extension';
import { withRouter } from 'react-router';
import { withTranslation, Trans, useTranslation } from 'react-i18next';
import MediaQuery from 'react-responsive'

import { makeGetCurrentUser, makeGetResolvedProjectUsers } from '../selectors/users';
import { makeGetTask, makeIsDependant } from '../selectors/tasks';
import { makeGetMilestone } from '../selectors/milestones';
import { makeGetTaskNotifications } from '../selectors/notifications';
import { makeGetSystemMessagesByTypeAndAssociationId } from '../selectors/messages';
import { makeIsSubscribed } from '../selectors/subscriptions';
import { makeGetProject } from '../selectors/projects';

import { removeFile } from '../actions/files';
import { fetchTask, updateTask, moveTask, setTaskVisibility, addTaskDoc, removeTaskDoc } from '../actions/tasks';
import { subscribe, unsubscribe } from '../actions/subscriptions';
import { setCustomFieldValue } from '../actions/custom-fields';
import { showModal } from '../actions/modal';
import { uploadFiles } from '../actions/files';

import { trackEvent } from '../services/events-tracker-service';
import { isTempId, scrollTo, capitalize } from '../utils/helpers';
import { createSlug, copyUrlToClipboard } from '../utils';
import { buildAppUrl } from '../utils/url';
import { getFile } from '../services/store-service';
import eventBus from '../services/event-bus';
import { createAlert } from '../services/alert-service';
import { isFileImage } from '../utils/file';
import { getUserPreference, setUserPreference } from '../services/user-preferences-service';

import { LocaleContext } from 'context/LocaleContext';
import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';

import DocContainer from '../containers/doc-container';

import DocCard from './doc-card';
import Spinner from './spinner';
import DescriptionPane from './description-pane';
import LabelPicker from './label-picker';
import LabelsList from './labels-list';
import Discussion from './discussion';
import Subtasks from './subtasks';
import MultiUpload from './multi-upload';
import AttachmentThumb from './attachment-thumb';
import SelectMilestonePopout from './select-milestone-popout';
import SelectTaskPopout from './select-task-popout';
import CanUser from './can-user';
import FeatureAvailable from './feature-available';
import MilestoneName from './milestone-name';
import TaskName from './task-name';
import DropPanel from './drop-panel';
import Tooltip from './tooltip';
import HelpTooltip from './help-tooltip';
import TimeTrackerCta from './time-tracker-cta';
import TaskNav from './task-nav';
import SubtasksOverview from './subtasks-overview';
import CustomFields from './custom-fields';
import CustomFieldsUpgradeNotice from './custom-fields-upgrade-notice';
import HideFrom from './hide-from';
import TaskCompleteToggle from './TaskCompleteToggle';
import MilestoneSingle from './milestone-single';
import MultiLineInput from 'components/MultiLineInput';
import TaskNiceId from 'components/TaskNiceId';
import TaskDates from 'components/TaskDates';
import SubtaskSingle from 'components/SubtaskSingle';
import TaskTaskGroup from 'components/TaskTaskGroup';
import NotificationsReader from 'components/NotificationsReader';
import TaskActions from 'components/TaskActions';
import TaskAssignees from 'components/TaskAssignees';
import TaskEmbed from 'components/TaskEmbed';
import TaskMinimize from 'components/TaskMinimize';
import LinkedTasks from 'components/LinkedTasks';
import PlanUpgradeButton from 'components/PlanUpgradeButton';

import IconUser from '../icons/icon-user';
import IconCalendarSimple from '../icons/icon-calendar-simple';
import IconDiscussionAlt from '../icons/icon-discussion-alt';
import IconRoadmap from '../icons/icon-roadmap';
import IconTask from '../icons/icon-task';
import IconEye from '../icons/icon-eye';
import IconFile from '../icons/icon-file';
import IconLabel from '../icons/icon-label';
import IconCustomField from '../icons/icon-custom-field';
import IconToggle from '../icons/icon-toggle';
import IconBell from '../icons/icon-bell';
import IconArrowLeft from '../icons/icon-arrow-left';
import IconClose from '../icons/icon-close';
import IconNumber from '../icons/icon-number';
import IconExpand from '../icons/icon-expand';
import IconCollapse from '../icons/icon-collapse';

const EMPTY_ARRAY = [];

const TASK_DISCUSSION_UTILS = ['emoji', 'file', 'doc'];

const ALLOWED_ACTIONS = ['addTimeEntry', 'viewTimeReport', 'taskLink', 'move', 'duplicate', 'copyURL', 'archive', 'remove'];
const SMALL_SCREEN_ALLOWED_ACTIONS = ['move', 'duplicate', 'copyURL', 'archive', 'remove']

const EmptyComponent = () => (
    <div className="discussion-placeholder">
        <div className="message placeholder-with-content">
            <div className="message-inner">
                <div className="message-side">
                    <div className="message-user">
                        <span className="placeholder-particle" />
                    </div>
                </div>

                <div className="message-main">
                    <p>
                        <Trans i18nKey="chat:empty.comments" />
                    </p>
                </div>
            </div>
        </div>
    </div>
);

const TaskDiscussion = ({ id, projectId, activityVisible, projectUsers, withScrollTo, onNewComment, onDiscussionFocus }) => {
    const { t } = useTranslation('chat');
    const localeCtx = useContext(LocaleContext);


    if (!id) {
        return null;
    }

    return <Discussion
        key={id}
        id={id}
        projectId={projectId}
        type="task"
        inputPlaceholer={t('placeholder.comment')}
        dateFormat={localeCtx.dateTimeFormats.dateTime}
        autofocus={false}
        excludeRemoved={true}
        botUseUserName={true}
        smallerSystem={true}
        directUpload={true}
        hideSystem={!activityVisible}
        utils={TASK_DISCUSSION_UTILS}
        canSendAsEmail={true}
        users={projectUsers}
        emptyComponent={<EmptyComponent />}
        onNewMessage={withScrollTo ? onNewComment : undefined}
        onFocus={withScrollTo ? onDiscussionFocus : undefined}
    />;
}

class TaskSingle extends PureComponent {
    static contextType = RolesAndPermissionsContext;
    static defaultProps = {
        shouldChangeUrl: true,
        large: true,
        section: 'subtasks'
    };

    state = {
        name: this.props.task ? this.props.task.name : '',
        currentSection: this.props.section,
        activityVisible: getUserPreference('task_activity_visible') || false,
        collapsed: getUserPreference('content_panel_collapsed') || false,
        currentSubtaskId: this.props.subtaskId,
        addingEmbed: false
    };

    handleNameBlur = name => {
        this.editTask({ name });
    }

    handleMilestoneSelect = milestoneId => {
        if (!this.props.task) {
            return;
        }

        if (this.props.task.milestone === milestoneId) {
            return;
        }

        this.updateTask({
            milestone: milestoneId
        });
    }

    handleMilestoneUntie = () => {
        if (!this.props.task.milestone) {
            return;
        }

        this.updateTask({
            milestone: null
        });
    }

    handleTaskSelect = taskId => {
        if (taskId && this.props.task.dependency === taskId) {
            return;
        }

        this.updateTask({
            dependency: taskId
        });
    }

    handleTaskUntie = () => {
        if (!this.props.task.dependency) {
            return;
        }

        this.updateTask({
            dependency: null
        });
    }

    handleStoryPointsChange = event => {
        const story_points = parseInt(event.target.value, 10);

        if (isNaN(story_points)) {
            return;
        }

        this.updateTask({ story_points });
    }

    handleStoryPointsKeyDown = event => {
        if (event.which === 13) {
            event.target.blur();
        }
    }

    handleNewComment = () => {
        setTimeout(() => {
            this.scrollToBottom();
        });
    }

    handleDiscussionFocus = () => {
        this.scrollToBottom();
    }

    handleDescriptionChange = description => {
        this.editTask({ description });
    }

    handleSubscribeToggleClick = () => {
        if (this.props.subscribed) {
            unsubscribe('tasks', this.props.id);
        } else {
            subscribe('tasks', this.props.id);
        }
    }

    handleTaskReveal = roleIds => {
        setTaskVisibility(this.props.id, true, roleIds);
    }

    handleTaskHide = roleIds => {
        setTaskVisibility(this.props.id, false, roleIds);
    }

    handleToggleActivityClick = () => {
        setUserPreference('task_activity_visible', !this.state.activityVisible);

        this.setState({
            activityVisible: !this.state.activityVisible
        });
    }

    handleAddSubtaskClick = () => {
        this.subtasksCtrl.addSubtask();
    }

    handleUpload = newAttachments => {
        if (!newAttachments) {
            return;
        }

        this.updateTask({
            banner: (this.props.task && this.props.task.banner) ? this.props.task.banner : (this.getBannerFromAttachments(newAttachments) || undefined)
        }, true);

        newAttachments.forEach(file => {
            trackEvent('FILE.UPLOAD', {
                via: 'Task',
                id: file.id,
                fileType: fileExtension(file.name),
                fileSize: file.size
            });
        });
    }

    handleInternalDocSelect = docId => {
        addTaskDoc(this.props.id, docId);
    }

    handleDocRemove = docId => {
        removeTaskDoc(this.props.id, docId);
    }

    handleSubtaskCommentsClick = subtaskId => {
        if (this.props.withoutSubtaskOpen) {
            return;
        }

        this.setState({
            currentSubtaskId: subtaskId
        });
    }

    handleSubtaskClose = () => {
        this.setState({
            currentSubtaskId: null
        });
    }

    handleSubtaskDelete = () => {
        trackEvent('SUBTASK.REMOVE', {
            via: 'Task'
        });

        this.setState({
            currentSubtaskId: null
        });
    }

    handleRemove = () => {
        trackEvent('TASK.REMOVE', {
            via: 'Task'
        });

        this.close();
    }

    close() {
        if (this.props.onBack) {
            this.props.onBack();
            return;
        }

        this.props.onClose && this.props.onClose();
    }

    getBannerFromAttachments(attachments) {
        if (!attachments) {
            return null;
        }

        const attachment = attachments.find(attachment => isFileImage(attachment.name));
        return attachment ? attachment.url : null;
    }

    handleFileDelete = attachmentId => {
        const file = getFile(attachmentId)
        trackEvent('FILE.DELETE', {
            via: 'Task',
            fileName: file && file.name
        });

        const filteredAttachments = this.props.task.attachments.filter(attachment => attachment && attachment.id !== attachmentId);

        this.updateTask({
            attachments: filteredAttachments,
            banner: this.getBannerFromAttachments(filteredAttachments)
        }, true);

        removeFile(attachmentId);
    }

    handleTaskNavClick = sectionId => {
        this.scrollToSection(sectionId);
    }

    handleScroll = () => {
        if (this.autoscrolling) {
            return;
        }

        const scrollTop = this.scrollElement.scrollTop + this.scrollElement.offsetTop + (this.navElement?.clientHeight || 0);
        const nodes = Array.from(this.scrollElement.querySelectorAll('.task-pane'));
        const currentSection = nodes[nodes.findIndex(section => section.offsetTop > scrollTop) - 1];
        const sectionId = currentSection && currentSection.getAttribute('data-section');

        if (this.state.currentSection !== sectionId) {
            this.setState({
                currentSection: sectionId
            });
        }   
    }

    handleCommentClick = () => {
        this.scrollToBottom();
    }

    handleTaskMoveClick = () => {
        if (this.props.task.archived || !this.taskMoveToProjectCtrl) {
            return;
        }

        this.taskMoveToProjectCtrl.show();
    }

    handleToggleComplete = (completed, taskGroupId) => {
        const newProps = {
            completed,
            task_group: taskGroupId,
        };

        if (completed) {
            newProps.completed_on = new Date();
        }

        this.updateTask(newProps);

        if (completed) {
            this.close();

            trackEvent('TASK_SINGLE.COMPLETE');
        }
    }

    handleTaskGroupChange = (taskGroupId, projectId) => {
        this.moveToTaskGroup(taskGroupId, projectId);
    }

    handleCustomFieldChange = (id, value) => {
        setCustomFieldValue('task', this.props.id, id, value);
    }

    handleCustomFieldCreateClick = () => {
        this.customFieldsCtrl && this.customFieldsCtrl.create();
    }

    handleSizeToggle = () => {
        setUserPreference('content_panel_collapsed', !this.state.collapsed);

        this.setState({
            collapsed: !this.state.collapsed
        });
    }

    handleFilesAdd = files => {
        uploadFiles(files, {
            task: this.props.id
        });
    }

    handleMilestoneDepClick = () => {
        this.setState({
            currentMilestoneId: this.props.dependantOn.id,
            currentSubtaskId: null
        }, () => {
            this.setState({
                subViewVisible: true
            });
        });
    }

    handleTaskDepClick = () => {
        this.setState({
            currentTaskId: this.props.dependantOn.id,
            currentSubtaskId: null
        }, () => {
            this.setState({
                subViewVisible: true
            });
        });
    }

    handleTaskClose = () => {
        this.setState({
            subViewVisible: false
        }, () => {
            this.setState({
                currentTaskId: null
            });
        });
    }

    handleMilestoneClose = () => {
        this.setState({
            subViewVisible: false
        }, () => {
            this.setState({
                currentMilestoneId: null
            });
        });
    }

    handleEmbedAddClick = () => {
        this.setState({
            addingEmbed: true
        });
    }

    handleEmbedRemove = () => {
        this.setState({
            addingEmbed: false
        });
    }

    moveToTaskGroup(taskGroupId, projectId) {
        const { t } = this.props;

        if (projectId && projectId !== this.props.task.project) {
            this.updateTask({
                project: projectId,
                assignees: [],
                milestone: null,
                order: Number.POSITIVE_INFINITY
            }, true);

            this.props.history.replace(`/${this.props.task.project}/task/${this.props.task.id}-${createSlug(this.props.task.name)}`);
        }

        // updateTask(this.props.id, {
        //     task_group: taskGroupId
        // })
        moveTask(this.props.id, {
            type: 'task_group',
            id: taskGroupId
        })
            .then(() => {
                createAlert('success', `<i class="icon material-icons">check_circle</i> ${t('Successfully moved task')}`);
            });
    }

    scrollToSection(sectionId) {
        this.autoscrolling = true;

        this.scrollTo(this.scrollElement.querySelector(`.section-${sectionId}`).offsetTop - this.scrollElement.offsetTop - this.navElement.clientHeight);

        this.setState({
            currentSection: sectionId
        });

        setTimeout(() => {
            this.autoscrolling = false;
        }, 1500);
    }

    scrollToBottom() {
        if (!this.scrollElement) {
            return;
        }

        this.scrollTo(this.scrollElement.scrollHeight);
    }

    scrollTo(position) {
        scrollTo(this.scrollElement, position);
    }

    taskAttachment(attachment, index) {
        return (
            <AttachmentThumb
                {...attachment}
                key={attachment.id}
                noRedirect={this.props.noRedirect}
                filePreviewPath={`/${this.props.task.project}/task/${this.props.id}/files`}
                association="Task"
                onDelete={this.handleFileDelete}
                siblings={this.props.task.attachments.map(a => a.id)}
            />
        )
    }

    editTask(taskProps) {
        if ('assignees' in taskProps) {
            trackEvent('TASK.USERS_ASSIGN');
        }

        if ('description' in taskProps) {
            trackEvent('TASK.DESCRIPTION_EDIT');
        }

        if ('due_date' in taskProps) {
            if (this.props.task && !this.props.task.due_date) {
                trackEvent('TASK.DEADLINE_ADD');
            } else if (!taskProps.due_date) {
                trackEvent('TASK.DEADLINE_REMOVE');
            } else {
                trackEvent('TASK.DEADLINE_EDIT');
            }
        }

        this.updateTask(taskProps);
    }

    focus() {
        if (this.nameInputCtrl) {
            this.nameInputCtrl.focus();
        }
    }

    updateTask(taskProps, preventSave = false) {
        return updateTask(this.props.id, taskProps, preventSave);
    }

    attemptFetchTask(force) {
        if (this.props.public) {
            return;
        }

        if (
            !force && (
                !this.props.id ||
                (this.props.task && !(this.props.task.shouldRefetch && !this.props.task.loading))
            )
        ) {
            return;
        }

        fetchTask(this.props.id);
    }

    init() {
        setTimeout(() => {
            eventBus.emitEvent('task:open');
        }, 400);

        // Change url
        if (!this.props.noRedirect && this.props.shouldChangeUrl && this.props.location.pathname === `${this.props.task.project}/task/${this.props.id}`) {
            this.props.history.replace(`/${this.props.task.project}/task/${this.props.id}-${createSlug(this.props.task.name)}`);
        }
    }

    setSubtasksCtrl = ctrl => this.subtasksCtrl = ctrl;
    setNavElement = el => this.navElement = el;
    setCustomFieldsCtrl = ctrl => this.customFieldsCtrl = ctrl;
    setNameInputCtrl = ctrl => this.nameInputCtrl = ctrl;

    get isPersonal() {
        return !this.props.task?.task_group && !this.props.task?.task
    }

    get isCompleted() {
        return this.props.task.completed;
    }

    get taskStatusLabel() {
        const { task, t } = this.props;

        if (task.archived) {
            return t('common:Archived');
        }

        if (task.completed) {
            return t('common:Completed');
        }

        return t('common:Archived');
    }

    componentDidMount() {
        this.props.provideController && this.props.provideController({
            focus: () => {
                this.focus();
            }
        });

        if (this.props.id) {
            this.attemptFetchTask();
        }

        if (this.props.task && (this.props.task.error || this.props.task.removed)) {
            this.close();
            return;
        }

        if (this.props.task && !this.props.task.loading) {
            this.init()
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.task && (this.props.task.error || this.props.task.removed)) {
            this.close();
            return;
        }

        if (this.props.task && !this.props.task.loading && (!prevProps.task || prevProps.task.loading)) {
            this.init()
        }

        if (this.props.id) {
            if ((this.props.id !== prevProps.id && this.props.id !== this.props.niceId && prevProps.id !== this.props.niceId) || !prevProps.id) {
                this.attemptFetchTask();
                this.setState({
                    currentMilestoneId: null,
                    currentTaskId: null,
                    currentSubtaskId: this.props.subtaskId,
                    addingEmbed: false
                });
            } else {
                this.attemptFetchTask();
            }
        }

        if (!prevProps.subtaskId && this.props.subtaskId) {
            this.setState({
                currentSubtaskId: this.props.subtaskId
            });
        }

        if (this.props.task && this.props.task.name !== this.state.name) {
            this.setState({
                name: this.props.task.name
            });
        }
    }

    componentWillUnmount() {
        this.props.provideController && this.props.provideController(null);
    }

    get large() {
        if (this.props.public) {
            return false;
        }

        return this.props.large && !this.state.collapsed;
    }

    getControlAssignees() {
        const { task, t } = this.props;

        if (this.isPersonal || this.props.public) {
            return null;
        }

        if (!task.assignees.length && !this.context?.canUser('assignToTask', task.created_by)) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconUser />
                    </span>

                    <span className="control-text">
                        { t('common:Assignees') }
                    </span>
                </div>

                <div className="control-value">
                    <TaskAssignees
                        id={this.props.id}
                        maxUsersCount={8}
                        addUserText={ !task.assignees?.length ? t('common:Add') : undefined }
                        addBtnClassName={'action-text' + (task.assignees?.length ? ' with-icon' : '')}
                        withTooltip={false}
                    />
                </div>
            </div>
        );
    }

    getControlLabels() {
        const { task, t } = this.props;

        if (!task.labels?.length && (!this.context?.canUser('editTask', task.created_by) || this.props.public)) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconLabel />
                    </span>

                    <span className="control-text">
                        { t('labels:Tags') }
                    </span>
                </div>


                <CanUser type="editTask" customList={task.created_by}>
                    <LabelPicker
                        taskId={this.props.id}
                        assignedLabels={task.labels}
                        xAlign="left"
                        defaultXPosition="right"
                    >
                        <div className="control-value">
                            <LabelsList labels={task.labels} inverted={true}>
                                <span className="action-text">
                                    { t('common:Add') }
                                </span>
                            </LabelsList>
                        </div>
                    </LabelPicker>
                </CanUser>

                <CanUser type="editTask" customList={task.created_by} reverse={true}>
                    <LabelsList labels={task.labels} inverted={true} />
                </CanUser>
            </div>
        );
    }

    getControlMilestone() {
        const { task, project, t } = this.props;

        if (this.isPersonal || (project?.disabled_modules || []).includes('milestones')) {
            return null;
        }

        if (!task.milestone && !this.context?.canUser('editTask', task.created_by)) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconRoadmap />
                    </span>

                    <span className="control-text">
                        { t('milestone:Milestone') }
                    </span>
                </div>

                <div className="control-value">
                    {
                        task.milestone ? (
                            <MilestoneName id={task.milestone} />
                        ) : (
                            <span className="action-text">{ t('common:Tie') }</span>
                        )
                    }
                </div>

                <CanUser type="editTask" customList={task.created_by}>
                    <SelectMilestonePopout
                        title={ t('actions.Tie Task to a Milestone') }
                        projectId={task.project}
                        selected={task.milestone}
                        onSelect={this.handleMilestoneSelect}
                        onRemove={this.handleMilestoneUntie}
                        showCreate={true}
                        removeCopy={t('actions.Untie Milestone')}
                        xAlign={this.props.isSmallScreen ? 'right' : 'left'}
                        defaultXPosition={this.props.isSmallScreen ? 'left' : 'right'}
                    >
                        <div className="popout-toggle" />
                    </SelectMilestonePopout>
                </CanUser>
            </div>
        );
    }

    getControlDates() {
        const { task, t } = this.props;

        if (!task.due_date && !task.start_date && !task.reminder && !task.rule && !this.context?.canUser('editTask', task.created_by)) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconCalendarSimple />
                    </span>

                    <span className="control-text">
                        { t('common:Dates') }
                    </span>
                </div>

                <div className="control-value">
                    <TaskDates
                        id={task.id}
                        withFromNow={true}
                        dateFormat="fullDateTime"
                        xAlign={this.props.isSmallScreen ? 'left' : undefined}
                        defaultXPosition={this.props.isSmallScreen ? 'center' : undefined}
                        showQuickOptions={!this.props.isSmallScreen}
                    />
                </div>
            </div>
        );
    }

    getControlDependency() {
        const { task, t } = this.props;

        if (this.isPersonal) {
            return null;
        }

        if (!task.dependency && !this.context?.canUser('editTask', task.created_by)) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconTask />
                    </span>

                    <span className="control-text">
                        { t('common:Dependency') }
                    </span>
                </div>

                <div className="control-value">
                    {
                        task.dependency ? (
                            <TaskName id={task.dependency} />
                        ) : (
                            <span className="action-text">{ t('common:Set') }</span>
                        )
                    }
                </div>

                <CanUser type="editTask" customList={task.created_by}>
                    <SelectTaskPopout
                        title={ t('actions.Set Task Dependency') }
                        excluded={[task.id]}
                        projectId={task.project}
                        selected={task.dependency}
                        milestone={task.milestone}
                        onSelect={this.handleTaskSelect}
                        onUntie={this.handleTaskUntie}
                        xAlign={this.props.isSmallScreen ? 'left' : 'right'}
                        defaultXPosition={this.props.isSmallScreen ? 'right' : 'left'}
                    >
                        <div className="popout-toggle" />
                    </SelectTaskPopout>
                </CanUser>
            </div>
        );
    }

    getControlStoryPoints() {
        const { task, t } = this.props;

        if (this.isPersonal || this.props.public) {
            return null;
        }

        if (!task.story_points && !this.context?.canUser('editTask', task.created_by)) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconNumber />
                    </span>

                    <span className="control-text">
                        { t('common:Story Points') }

                        <HelpTooltip>
                            { t('common:storyPoints.help') } <a href="https://help.niftypm.com/en/articles/3827830-using-story-points-to-balance-resources-agile-scrum-workflow" target="_blank" rel="noopener noreferrer">{ t('common:Learn More') }</a>
                        </HelpTooltip>
                    </span>
                </div>

                <div className="control-value">
                    <input
                        key={this.props.id}
                        className="control-value-input"
                        type="text"
                        defaultValue={task.story_points}
                        disabled={!this.context?.canUser('editTask', task.created_by)}
                        placeholder={this.context?.canUser('editTask', task.created_by) ? t('common:Set') : ''}
                        onChange={this.handleStoryPointsChange}
                        onKeyDown={this.handleStoryPointsKeyDown}
                    />
                </div>
            </div>
        );
    }

    get controls() {
        const wideControlIndex = this.props.isSmallScreen ? 2 : 3;

        const assignees = this.getControlAssignees();
        const labels = this.getControlLabels();
        const milestone = this.getControlMilestone();
        const dates = this.getControlDates();
        const dependency = this.getControlDependency();
        const storyPoints = this.getControlStoryPoints();

        const controls = [assignees, labels, milestone, dependency, storyPoints].filter(Boolean);

        controls.splice(wideControlIndex, 0, dates);

        return controls.map((el, index) => (
            <div
                className={'control' + (index % wideControlIndex === 0 ? ' wider' : (index + 1) % wideControlIndex === 0 ? ' narrower' : '')}
            >
                { el }
            </div>
        ));

    }

    get element() {
        const { task } = this.props;

        if (!task) {
            return <Spinner centered={true} />;
        }

        if (!('name' in task) || task.error) {
            return <Spinner centered={true} />;
        }

        const { dependantOn, t } = this.props;

        return (
            <div className={'content-panel' + (dependantOn ? ' dependant' : '')}>
                <div className="content-panel-inner">
                    <div className="content-panel-main">
                        <div className="content-panel-head">
                            {
                                dependantOn ? (
                                    <div className="content-panel-additional-info">
                                        <Trans
                                            i18nKey="task:dependant.text"
                                            values={{
                                                type: t(`common:${capitalize(dependantOn.type)}`).toLowerCase()
                                            }}
                                            components={{
                                                nameWrap: <strong className="clickable" onClick={dependantOn.type === 'milestone' ? this.handleMilestoneDepClick : this.handleTaskDepClick} />,
                                                name: dependantOn.type === 'milestone' ? <MilestoneName id={dependantOn.id} /> : <TaskName id={dependantOn.id} />
                                            }}
                                        />

                                        <HelpTooltip theme="semi-light">
                                            { t(`dependant.help.${dependantOn.type}`) }
                                        </HelpTooltip>
                                    </div>
                                ) : null
                            }

                            <div className="content-panel-head-top">
                                <div className="content-panel-head-utils">
                                    {
                                        this.props.onBack ? (
                                            <button className="content-panel-control btn content-panel-back" onClick={this.props.onBack}><IconArrowLeft size="20px" /></button>
                                        ) : null
                                    }

                                    {
                                        this.props.task.nice_id && !this.isPersonal ? (
                                            <span className="nice-id-holder">
                                                <TaskNiceId niceId={this.props.task.nice_id} />
                                            </span>
                                        ) : null
                                    }

                                    <div className={'tag-pill action' + (task.completed ? ' completed' : '')} onClick={this.handleTaskMoveClick}>
                                        {
                                            !task.archived && !task.completed ? (
                                                <TaskTaskGroup
                                                    key={this.props.id}
                                                    id={this.props.id}
                                                    provideController={ctrl => this.taskMoveToProjectCtrl = ctrl}
                                                    nameFallback={this.isPersonal ? t('common:Personal') : null}
                                                    onChange={this.handleTaskGroupChange}
                                                    onConfirm={this.handleToggleComplete}
                                                />
                                            ) : this.taskStatusLabel
                                        }
                                    </div>
                                </div>

                                <div className="content-panel-controls">
                                    <div className="content-panel-simple-actions">
                                        <CanUser type="startStopTimer">
                                            {/*<HarvestButton taskId={this.props.id} projectId={this.props.task.project} />*/}

                                            <FeatureAvailable type="timeTracking">
                                                <div className="content-panel-simple-action">
                                                    <div className="content-panel-simple-action-inner">
                                                        <TimeTrackerCta key={this.props.id} taskId={this.props.id} projectId={this.props.task.project} iconOnly={true} />
                                                    </div>
                                                </div>
                                            </FeatureAvailable>

                                            <FeatureAvailable type="timeTracking" reverse={true}>
                                                <div className="content-panel-simple-action">
                                                    <Tooltip
                                                        className="light help-tooltip"
                                                        tooltip={
                                                            <Trans
                                                                i18nKey="billing:upgrade.timeTracking"
                                                                components={{
                                                                    title: <strong />,
                                                                    button: <PlanUpgradeButton reason="timeTracking" />,
                                                                    learnMore: <a href="http://help.niftypm.com/en/articles/3059016-time-tracking-reporting" target="_blank" rel="noopener noreferrer" />
                                                                }}
                                                                values={{ plan: t('billing:plans.Starter.title') }}
                                                            />
                                                        }
                                                        placement="bottom-start"
                                                    >
                                                        <div className="content-panel-simple-action-inner">
                                                            <TimeTrackerCta placeholder={true} />
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            </FeatureAvailable>
                                        </CanUser>

                                        {
                                            !this.isPersonal && !this.props.public ? (
                                                <Tooltip tooltip={this.props.subscribed ? <span>{ t('Unsubscribe description') }</span> : <span><Trans i18nKey="task:Subscribe description" /></span>}>
                                                    <div className={'content-panel-simple-action' + (this.props.subscribed ? ' active' : '')} onClick={this.handleSubscribeToggleClick}>
                                                        <div className="content-panel-simple-action-inner">
                                                            <IconBell />
                                                        </div>
                                                    </div>
                                                </Tooltip>
                                            ) : null
                                        }

                                        {
                                            !this.isPersonal ? (
                                                <CanUser type="hideTask">
                                                    <div className={'content-panel-simple-action' + (task.hidden?.length ? ' active' : '')}>
                                                        <HideFrom
                                                            onHide={this.handleTaskHide}
                                                            onReveal={this.handleTaskReveal}
                                                            hidden={this.props.task.hidden}
                                                            creator={this.props.task.created_by}
                                                        >
                                                            <div>
                                                                <Tooltip tooltip={task.hidden?.length ? (
                                                                    <span>{ t('Task hidden from', { roles: this.context?.getRolesListString(this.props.task.hidden, true) }) }</span>
                                                                ) : (
                                                                    <span>{ t('Hide task from') }</span>
                                                                )}>
                                                                    <div className="content-panel-simple-action-inner">
                                                                        <IconEye size="16px" />
                                                                    </div>
                                                                </Tooltip>
                                                            </div>
                                                        </HideFrom>
                                                    </div>
                                                </CanUser>
                                            ) : null
                                        }
                                    </div>

                                    <TaskActions
                                        id={this.props.id}
                                        className="content-panel-control"
                                        innerClassName="content-panel-actions-trigger"
                                        allowedActions={this.props.isSmallScreen ? SMALL_SCREEN_ALLOWED_ACTIONS : ALLOWED_ACTIONS}
                                        onRemove={this.handleRemove}
                                    />

                                    {
                                        this.props.large && !this.props.inside && !this.props.public ? (
                                            <Tooltip tooltip={this.state.collapsed ? t('common:Expand') : t('common:Collapse')} withTooltipHover={false}>
                                                <button className="content-panel-control btn content-panel-size-toggle" onClick={this.handleSizeToggle}>
                                                    {
                                                        this.state.collapsed ? (
                                                            <IconExpand />
                                                        ) : (
                                                            <IconCollapse />
                                                        )
                                                    }
                                                </button>
                                            </Tooltip>
                                        ) : null
                                    }

                                    {
                                        this.props.onClose ? (
                                            <TaskMinimize id={this.props.id} closeTask={this.props.onClose} />
                                        ) : null
                                    }

                                    {
                                        this.props.onClose ? (
                                            <button className="content-panel-control btn content-panel-close" onClick={this.props.onClose}><IconClose /></button>
                                        ) : null
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="content-panel-main-inner">
                            <div className="content-panel-side">
                                <div className="content-panel-title">
                                    <CanUser type="completeTask" customList={task.created_by}>
                                        <div className="content-panel-complete-shortcut">
                                            <TaskCompleteToggle
                                                id={this.props.id}
                                                checkSubtasks={true}
                                                dependantOn={dependantOn}
                                                completed={task.completed}
                                                projectId={task.project}
                                                size="large"
                                                onChange={this.handleToggleComplete}
                                            />
                                        </div>
                                    </CanUser>

                                    <MultiLineInput
                                        className="content-panel-field-input"
                                        placeholder={t('Add task name')}
                                        canEdit={this.context?.canUser('editTask', [task.created_by])}
                                        value={task.name}
                                        provideController={this.setNameInputCtrl}
                                        onBlur={this.handleNameBlur}
                                    />
                                </div>

                                <div className="content-panel-scrollable nice-scroll" ref={element => this.scrollElement = element} onScroll={this.handleScroll}>
                                    <div className="content-panel-block">
                                        <DescriptionPane
                                            key={this.props.id}
                                            editable={this.context?.canUser('editTask', [task.created_by])}
                                            placeholder={t('Add task description')}
                                            content={task.description || ''}
                                            onChange={this.handleDescriptionChange}
                                            onFilesPaste={this.handleFilesAdd}
                                        />
                                    </div>

                                    <div className="content-panel-block">
                                        <div className="content-panel-utils">
                                            { this.controls }
                                        </div>
                                    </div>

                                    {
                                        task.linked_tasks?.length ? (
                                            <LinkedTasks key={this.props.id} taskId={this.props.id} />
                                        ) : null
                                    }

                                    {
                                        !this.isPersonal ? (
                                            <div className="content-panel-nav-holder" ref={this.setNavElement}>
                                                <TaskNav public={this.props.public} onClick={this.handleTaskNavClick} current={this.state.currentSection} />
                                            </div>
                                        ) : null
                                    }

                                    <div className="content-panel-body">
                                        <div className="content-panel-body-main">
                                            <div className="content-panel-section section-subtasks" data-section="subtasks">
                                                <SubtasksOverview taskId={this.props.id} />

                                                <div className="section-body">
                                                    <div className="content-panel-subtasks">
                                                        <Subtasks
                                                            projectId={this.props.task.project}
                                                            taskId={this.props.id}
                                                            page={this.props.page}
                                                            isPublic={this.props.public}
                                                            onCommentsClick={!this.props.withoutSubtaskOpen && !this.props.public && this.handleSubtaskCommentsClick}
                                                            provideController={this.setSubtasksCtrl}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {
                                                !this.props.public ? (
                                                    <React.Fragment>
                                                        {
                                                            !this.isPersonal ? (
                                                                <div className="content-panel-section section-files" data-section="files">
                                                                    <div className="section-head">
                                                                        <h4>
                                                                            <span className="title-icon"><IconFile /></span>

                                                                            <span className="text">{ t('common:Attachments') }</span>

                                                                            <MultiUpload
                                                                                taskId={this.props.id}
                                                                                projectId={this.props.task.project}
                                                                                internalDocEnabled={true}
                                                                                onEmbedAdd={this.handleEmbedAddClick}
                                                                                btnClassName="btn-colored btn-circle btn-xxsmall btn-simple btn-icon"
                                                                                onUpload={this.handleUpload}
                                                                                onInternalDocSelect={this.handleInternalDocSelect}
                                                                            >
                                                                                +
                                                                            </MultiUpload>
                                                                        </h4>
                                                                    </div>

                                                                    <div className="section-body">
                                                                        <div className="content-panel-files">
                                                                            <DropPanel
                                                                                taskId={this.props.id}
                                                                                upload={true}
                                                                                className="drop-panel-default drop-panel-inline drop-panel-small"
                                                                                onUpload={this.handleUpload}
                                                                            >
                                                                                <span>
                                                                                    <i className="icon ion ion-android-attach" />

                                                                                    <Trans i18nKey="file:Drag & drop or choose files" components={{ u: <u /> }} />
                                                                                </span>
                                                                            </DropPanel>

                                                                            {
                                                                                (this.props.task.attachments || []).map((item, index) => this.taskAttachment(item, index))
                                                                            }

                                                                            {
                                                                                (this.props.task.documents || []).map(docId => (
                                                                                    <DocContainer key={docId} id={docId}>
                                                                                        <DocCard
                                                                                            removeTooltip={t('Remove From Task')}
                                                                                            onRemove={this.handleDocRemove}
                                                                                        />
                                                                                    </DocContainer>
                                                                                ))
                                                                            }

                                                                            {
                                                                                task.embed_url || this.state.addingEmbed ? <TaskEmbed id={task.id} onRemove={this.handleEmbedRemove} /> : null
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : null
                                                        }

                                                        {
                                                            !this.isPersonal ? (
                                                                <div className="content-panel-section section-fields" data-section="fields">
                                                                    <div className="section-head">
                                                                        <h4>
                                                                            <span className="title-icon"><IconCustomField /></span>

                                                                            <span className="text">{ t('fields:Custom Fields') }</span>

                                                                            <FeatureAvailable type="customFields">
                                                                                <CanUser type="createCustomFields">
                                                                                    <button className="btn btn-circle btn-xxsmall btn-simple btn-icon btn-colored" onClick={this.handleCustomFieldCreateClick}>+</button>
                                                                                </CanUser>
                                                                            </FeatureAvailable>
                                                                        </h4>
                                                                    </div>

                                                                    <div className="section-body">
                                                                        <FeatureAvailable type="customFields">
                                                                            <CustomFields
                                                                                key={this.props.id}
                                                                                projectId={this.props.task.project}
                                                                                type="row-single"
                                                                                association="task"
                                                                                associationId={this.props.id}
                                                                                values={this.props.task.fields}
                                                                                onChange={this.handleCustomFieldChange}
                                                                                provideController={this.setCustomFieldsCtrl}
                                                                            />
                                                                        </FeatureAvailable>

                                                                        <FeatureAvailable type="customFields" reverse={true}>
                                                                            <CustomFieldsUpgradeNotice />
                                                                        </FeatureAvailable>
                                                                    </div>
                                                                </div>
                                                            ) : null
                                                        }

                                                        {
                                                            this.props.public ? null : (
                                                                <div className="content-panel-section section-comments hide-on-large" data-section="comments">
                                                                    <div className="section-head">
                                                                        <h4>
                                                                            <span className="title-icon"><IconDiscussionAlt /></span>

                                                                            <span className="text">{ t('chat:Comment_count', { count: this.props.task.comments }) }</span>
                                                                        </h4>

                                                                        <div className="section-actions">
                                                                            <button className="btn btn-small section-action-btn" onClick={this.handleToggleActivityClick}>
                                                                                <IconToggle />

                                                                                {
                                                                                    this.state.activityVisible ? t('notifications:Hide Activity') : t('notifications:Show Activity')
                                                                                }
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    <div className="section-body">
                                                                        <TaskDiscussion
                                                                            id={this.props.id}
                                                                            projectId={this.props.task.project}
                                                                            withScrollTo={true}
                                                                            activityVisible={this.state.activityVisible}
                                                                            projectUsers={this.props.projectUsers}
                                                                            onNewComment={this.handleNewComment}
                                                                            onDiscussionFocus={this.handleDiscussionFocus}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )
                                                        }
                                                    </React.Fragment>
                                                ) : null
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <MediaQuery minWidth={1500}>
                                {
                                    this.large ? (
                                        <div className="content-panel-side">
                                            <div className="content-panel-section section-comments">
                                                <div className="section-head">
                                                    <h4>
                                                        <span className="title-icon"><IconDiscussionAlt /></span>

                                                        <span className="text">{ t('chat:Comment_count', { count: this.props.task.comments }) }</span>
                                                    </h4>

                                                    <div className="section-actions">
                                                        <button className="btn btn-small section-action-btn" onClick={this.handleToggleActivityClick}>
                                                            <IconToggle />

                                                            {
                                                                this.state.activityVisible ? t('notifications:Hide Activity') : t('notifications:Show Activity')
                                                            }
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="section-body">
                                                    <TaskDiscussion
                                                        id={this.props.id}
                                                        projectId={this.props.task.project}
                                                        withScrollTo={false}
                                                        activityVisible={this.state.activityVisible}
                                                        projectUsers={this.props.projectUsers}
                                                        onNewComment={this.handleNewComment}
                                                        onDiscussionFocus={this.handleDiscussionFocus}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : null
                                }
                            </MediaQuery>
                        </div>
                    </div>

                    <div className={'content-panel content-panel-subview' + (this.state.currentSubtaskId ? ' visible' : '')}>
                        {
                            this.state.currentSubtaskId ? (
                                <SubtaskSingle
                                    id={this.state.currentSubtaskId}
                                    isSmallScreen={this.props.isSmallScreen}
                                    discussionElement={<TaskDiscussion
                                        id={this.state.currentSubtaskId}
                                        projectId={this.props.task.project}
                                        withScrollTo={false}
                                        activityVisible={this.state.activityVisible}
                                        projectUsers={this.props.projectUsers}
                                        onNewComment={this.handleNewComment}
                                        onDiscussionFocus={this.handleDiscussionFocus}
                                    />}
                                    onClose={this.handleSubtaskClose}
                                    onDelete={this.handleSubtaskDelete}
                                />
                            ) : null
                        }
                    </div>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className={'content-panel-holder' + (this.props.id ? ' visible' : '') + (this.props.greaterZIndex ? ' greater-z-index' : '') + (this.props.inside ? ' inside' : '') + ((this.state.subViewVisible) ? ' subview-visible' : '') + (this.large ? ' large' : '')}>
                { this.element }

                {
                    this.props.id ? (
                        <React.Fragment>
                            <NotificationsReader notifications={this.props.notifications.map(({ id }) => id)} />

                            {
                                this.state.currentTaskId ? (
                                    <TaskSingleContainer
                                        id={this.state.currentTaskId}
                                        inside={true}
                                        large={this.large}
                                        noRedirect={true}
                                        shouldChangeUrl={false}
                                        public={this.props.public}
                                        onBack={this.handleTaskClose}
                                        onClose={this.props.onClose}
                                    />
                                ) : null
                            }

                            {
                                this.state.currentMilestoneId ? (
                                    <MilestoneSingle
                                        id={this.state.currentMilestoneId}
                                        inside={true}
                                        large={this.large}
                                        onBack={this.handleMilestoneClose}
                                        onClose={this.props.onClose}
                                    />
                                ) : null
                            }
                        </React.Fragment>
                    ) : null
                }
            </div>
        )
    }
};

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getTaskOrSubtask = makeGetTask();
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();
    const isDependant = makeIsDependant();
    const getTaskNotifications = makeGetTaskNotifications();
    const getSystemMessagesByTypeAndAssociationId = makeGetSystemMessagesByTypeAndAssociationId();
    const isSubscribed = makeIsSubscribed();
    const getProject = makeGetProject();
    const getResolvedProjectUsers = makeGetResolvedProjectUsers();

    return ({ users, projects, tasks, notifications, messages, subscriptions, subtasks, milestones }, ownProps) => {
        const argTaskId = ownProps.id;
        const task =  argTaskId && getTaskOrSubtask(tasks.items, argTaskId);
        const openedFromSubtask = Boolean(task?.task);
        const originalId = isTempId((task && task.id) || argTaskId) ? null : (task && task.id) || argTaskId;
        const taskId = task?.task || originalId;
        const projectId = task && task.project ? task.project : ownProps.projectId;
        const project = getProject(projects.items, projectId);

        return {
            id: taskId,
            niceId: argTaskId,
            currentUser: getCurrentUser(users),
            subtaskId: ownProps.subtaskId || (openedFromSubtask && originalId),
            task: taskId === task?.id ? task : getTask(tasks.items, taskId),
            projectId,
            project,
            milestone: task && task.milestone && getMilestone(milestones.items, task.milestone),
            dependantOn: task && isDependant(taskId, true, tasks.items, milestones.items),
            notifications: taskId ? getTaskNotifications(notifications.items, taskId, true, true) : EMPTY_ARRAY,
            activities: taskId ? getSystemMessagesByTypeAndAssociationId(messages.items, 'task', taskId) : EMPTY_ARRAY,
            subscribed: taskId && isSubscribed(subscriptions, taskId),
            projectUsers: project && !users.items.some(user => user.loading) ? getResolvedProjectUsers(project, users) : EMPTY_ARRAY

        }
    }
};

const TaskSingleContainer = withRouter(connect(makeMapStateToProps)(withTranslation(['task', 'milestone', 'time-tracking', 'chat', 'common', 'billing', 'file', 'fields', 'notifications', 'recurring', 'labels'])(TaskSingle)));

export default TaskSingleContainer;
